import { BACKEND_URL, ENVIRONMENT } from "../lib/config"

/**
 * Where the API client points, and the session it carries.
 *
 * Read once, synchronously, while the hey-api client is being configured
 * (src/hey-api.ts) — before React mounts. That is why this is a plain
 * localStorage read and not a jotai atom: there is no store to read from yet.
 *
 * There used to be three `atomWithStorage` atoms on these same keys. Nothing
 * ever imported them, and jotai's default storage JSON-encodes, so the first
 * component to write one would have stored `"http://…"` with the quotes
 * attached — and this reader, which takes the raw string, would have handed
 * those quotes to fetch as a base URL. They are deleted rather than repaired:
 * a reactive mirror of a value that is read before React starts cannot be kept
 * honest, and an unused one is only a trap for whoever reaches for it first.
 *
 * `EnvSwitcher` is the only writer, and it writes plain strings.
 */

export const BASE_URL_KEY = "condri-base-url"
export const AUTH_TOKEN_KEY = "condri-api-auth-token"
export const ENVIRONMENT_KEY = "condri-environment"

/**
 * A stored string, or null when there is nothing usable.
 *
 * Two things this absorbs. localStorage *throws* rather than returning null in
 * a browser with site data blocked, and an exception here would leave the API
 * client unconfigured — falling back to the build's own defaults is the right
 * answer for that viewer. And a JSON-encoded value is unwrapped: nothing writes
 * one today, but a value left in someone's browser by an older build would
 * otherwise arrive with its quotes and point every request at nowhere.
 */
export function readStored(key: string): string | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(key)
  } catch {
    return null
  }
  if (raw === null || raw === "") return null
  // A URL never starts with a quote, so this only ever catches JSON.
  if (raw.startsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(raw)
      return typeof parsed === "string" && parsed !== "" ? parsed : null
    } catch {
      return null
    }
  }
  return raw
}

class PreloadStore {
  get url() {
    return readStored(BASE_URL_KEY) || BACKEND_URL
  }
  get authToken() {
    return readStored(AUTH_TOKEN_KEY) || ""
  }
  get environment() {
    return readStored(ENVIRONMENT_KEY) || ENVIRONMENT
  }
}

export const preloadStore = new PreloadStore()
